import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { EmployeeBox, GapBox } from 'components/Box'
import { BoxForm, CodeField, NumberField, Select, Switch } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import { useAbility } from 'providers/casl'
import { CONTAINER_SPACING } from 'providers/material-ui/theme/constants'
import { useGetCounterStyles } from '../../hooks/useGetCounterStyles'
import { KNOWLEDGE_BASE_URL } from './constants'
import { Schema } from './schema'
import { SingleAdViewTimeSoftValidator } from './SingleAdViewTimeSoftValidator'

export const MultiAnchorSection = (): React.JSX.Element => {
  const ability = useAbility()
  const { t: tc } = useTranslation('common')
  const { t } = useTranslation('features/globalSetup', {
    keyPrefix: 'defaults.richPlacements.multiAnchor',
  })

  const { control, watch } = useFormContext<Schema>()
  const { frequencyCappingEnabled } = watch()

  const counterStyles = useGetCounterStyles()

  const cannotUpdate: boolean = ability.cannot(
    'update',
    'GlobalSetupFeature-defaults-richPlacements',
  )
  const canReadFrequencyCapping: boolean = ability.can('read', 'GlobalSetupField-frequencyCapping')
  const cannotUpdateFrequencyCapping: boolean = ability.cannot(
    'update',
    'GlobalSetupField-frequencyCapping',
  )

  return (
    <>
      <Paper
        data-cy='section-multi-anchor'
        sx={{ mt: 0 }}
      >
        <SectionHeader
          subtitle={t('description')}
          title={t('title')}
        />
      </Paper>

      <Paper data-cy='section-basic-settings'>
        <SectionHeader
          subtitle={t('basicSettings.subtitle')}
          title={t('basicSettings.title')}
        />

        <GapBox />

        <BoxForm>
          <NumberField
            control={control}
            disabled={cannotUpdate}
            helperText={<SingleAdViewTimeSoftValidator />}
            label={t('basicSettings.singleAdViewTime.label')}
            name='singleAdViewTime'
            tooltip={{ content: t('basicSettings.singleAdViewTime.tooltip') }}
            width='md'
          />
          <Switch
            control={control}
            disabled={cannotUpdate}
            label={t('basicSettings.displayAfterUserInteraction.label')}
            labelPlacement='start'
            name='displayAfterUserInteraction'
            tooltip={{ content: t('basicSettings.displayAfterUserInteraction.tooltip') }}
          />
          {canReadFrequencyCapping && (
            <EmployeeBox>
              <BoxForm>
                <Switch
                  control={control}
                  disabled={cannotUpdate || cannotUpdateFrequencyCapping}
                  label={t('basicSettings.frequencyCapping.label')}
                  labelPlacement='start'
                  name='frequencyCappingEnabled'
                  tooltip={{ content: t('basicSettings.frequencyCapping.tooltip') }}
                />

                {frequencyCappingEnabled && (
                  <BoxForm
                    sx={{
                      ml: CONTAINER_SPACING,
                    }}
                  >
                    <NumberField
                      control={control}
                      disabled={cannotUpdate || cannotUpdateFrequencyCapping}
                      label={t('basicSettings.frequencyCapping.impressionLimit.label')}
                      name='impressionLimit'
                      tooltip={{
                        content: t('basicSettings.frequencyCapping.impressionLimit.tooltip'),
                      }}
                    />
                    <NumberField
                      control={control}
                      disabled={cannotUpdate || cannotUpdateFrequencyCapping}
                      label={t('basicSettings.frequencyCapping.timeWindow.label')}
                      name='timeWindow'
                      tooltip={{ content: t('basicSettings.frequencyCapping.timeWindow.tooltip') }}
                    />
                  </BoxForm>
                )}
              </BoxForm>
            </EmployeeBox>
          )}
        </BoxForm>
      </Paper>

      <Paper data-cy='section-appearance'>
        <SectionHeader
          subtitle={t('appearance.subtitle')}
          title={t('appearance.title')}
        />

        <GapBox />

        <BoxForm>
          <Select
            control={control}
            disabled={cannotUpdate}
            label={t('appearance.counterStyle.label')}
            name='counterStyle'
            options={counterStyles}
            tooltip={{ content: t('appearance.counterStyle.tooltip') }}
          />

          <Switch
            control={control}
            disabled={cannotUpdate}
            label={t('appearance.showCloseButton.label')}
            labelPlacement='start'
            name='showCloseButton'
            tooltip={{ content: t('appearance.showCloseButton.tooltip') }}
          />
          <Switch
            control={control}
            disabled={cannotUpdate}
            label={t('appearance.fullWidthMode.label')}
            labelPlacement='start'
            name='fullWidthMode'
            tooltip={{ content: t('appearance.fullWidthMode.tooltip') }}
          />
          <CodeField
            control={control}
            disabled={cannotUpdate}
            language='json'
            name='customStyling'
            title={t('appearance.customStyling.label')}
            tooltip={{
              content: t('appearance.customStyling.tooltip'),
              link: {
                title: tc('learnMore'),
                url: KNOWLEDGE_BASE_URL.CUSTOM_STYLING,
              },
            }}
          />
        </BoxForm>
      </Paper>
    </>
  )
}
